import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  ButtonWrapper,
  SearchInputWrapper,
  StyledBox,
  StyledButton
} from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { academicYears } from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import { allowedUserEmailIds } from './config/constant';
import {
  fetchOutstandingFeeReport,
  transformOutstandingFeeReportData
} from './utils/transformOutstandingFeeData';
import ToastMessage from '../../components/ToastMessage';
import OutStandingFeeReportTable from './components/OutStandingFeeReportTable';
import { getPaymentUser } from '../../apis/payments/users';
import { isEmpty } from 'lodash';
import CustomDatePicker from '../../components/DatePicker';
import moment from 'moment';
import { downloadReport } from '../../utils/reportUtils';
import { PageWrapper, Title } from '../common/styled';
import { STUDENT_STATUS_OPTIONS } from '../../constants/studentStatus';

const OutstandingFeeReport = () => {
  const [academicYear, setAcademicYear] = useState<string>('');
  const [studentStatus, setStudentStatus] = useState<string>('All');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [transformOutstandingFeeData, setTransformOutstandingFeeData] =
    useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  useEffect(() => {
    const fetchPaymentUser = async () => {
      const { email } = await getPaymentUser();
      setUserEmail(email);
    };
    fetchPaymentUser();
  }, []);

  const isAllowedUser = allowedUserEmailIds.includes(userEmail ?? '');
  const resolvedStatus = studentStatus === 'All' ? undefined : studentStatus;

  const handleSearch = async () => {
    if (!isAllowedUser) {
      showToast('Unauthorized access', SnackbarSeverityType.error);
      return;
    }
    setIsSearching(true);
    setTransformOutstandingFeeData([]);
    try {
      const date = selectedDate?.format('YYYY-MM-DD') || '';
      const response = await fetchOutstandingFeeReport(
        `${date} 18:29:59`,
        academicYear,
        resolvedStatus
      );

      const transformedData = transformOutstandingFeeReportData(response);
      setTransformOutstandingFeeData(transformedData);
      showToast(
        'Successfully fetched outstanding fee report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching outstanding fee report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    if (!isAllowedUser) {
      showToast('Unauthorized access', SnackbarSeverityType.error);
      return;
    }
    setIsDownloading(true);
    try {
      const date = selectedDate?.format('YYYY-MM-DD') || '';
      const response = await fetchOutstandingFeeReport(
        `${date} 18:29:59`,
        academicYear,
        resolvedStatus
      );

      const transformedData = transformOutstandingFeeReportData(response);

      if (isEmpty(transformedData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      const fileName = `OutstandingFeeReport-As_On-${moment().format(
        'YYYY-MM-DD'
      )}`;
      downloadReport(
        transformedData,
        fileName,
        'Outstanding Fee Report',
        showToast
      );
      showToast(
        'Successfully fetched outstanding fee report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching outstanding fee report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const isSearchButtonDisabled = isSearching || !academicYear || !selectedDate;
  const isDownloadButtonDisabled =
    isDownloading || !academicYear || !selectedDate || isSearching;

  return (
    <>
      <Title>Outstanding Fee Report</Title>
      <Box>
        <PageWrapper sx={{ minHeight: 'auto' }}>
          <SearchInputWrapper>
            <StyledBox>
              <DropdownSelect
                label='Academic Year'
                placeholder='Select Academic Year'
                name='academicYear'
                value={academicYear}
                options={academicYears}
                onChange={(event) => {
                  setAcademicYear(event.target.value as string);
                  setHasSearched(false);
                }}
                hasTopLabel
              />
              <DropdownSelect
                label='Status'
                placeholder='Select Status'
                name='studentStatus'
                value={studentStatus}
                options={STUDENT_STATUS_OPTIONS}
                onChange={(event) => {
                  setStudentStatus(event.target.value as string);
                  setHasSearched(false);
                }}
                hasTopLabel
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CustomDatePicker
                  label='As on Date'
                  onChange={(newValue: any) => {
                    setSelectedDate(newValue);
                    setHasSearched(false);
                  }}
                  value={selectedDate}
                />
              </LocalizationProvider>
            </StyledBox>
            <ButtonWrapper>
              <StyledButton
                onClick={handleSearch}
                disabled={isSearchButtonDisabled}
              >
                {isSearching ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'View Report'
                )}
              </StyledButton>
              <StyledButton
                onClick={handleDownload}
                disabled={isDownloadButtonDisabled}
              >
                {isDownloading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Download Report'
                )}
              </StyledButton>
            </ButtonWrapper>
          </SearchInputWrapper>
        </PageWrapper>
        {hasSearched && !isSearching && (
          <OutStandingFeeReportTable
            outStandingReportData={transformOutstandingFeeData}
          />
        )}
        <ToastMessage
          open={toastOpen}
          message={toastMessage}
          severity={toastSeverity}
          onClose={handleCloseToast}
        />
      </Box>
    </>
  );
};

export default OutstandingFeeReport;
